/**
 * Centralized SignalR Service
 * Singleton pattern to manage SignalR connection across all Vue components
 */
import * as signalR from "@microsoft/signalr";

class SignalRService {
  constructor() {
    this.connection = null;
    this.isConnecting = false;
    this.isConnected = false;
    this.connectionPromise = null;
    this.eventHandlers = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.urlApi = import.meta.env.VITE_APP_API || 'http://localhost:5000/';
    this.pingInterval = null;
    // Debug mode - set to true to enable logging
    this.debug = import.meta.env.VITE_SIGNALR_DEBUG === 'true' || false;
    
    // Role-based connection settings
    this.allowedRoles = [0, 1, 6]; // Super Admin, Admin, Operator
    this.excludedPaths = ['FullStream', 'Snippet', 'LoginSnippet', 'Login'];
  }

  /**
   * Log helper - only logs when debug mode is enabled
   * @private
   */
  log(...args) {
    if (this.debug) {
      console.log('[SignalR Service]', ...args);
    }
  }

  /**
   * Error log helper - always logs errors
   * @private
   */
  logError(...args) {
    console.error('[SignalR Service]', ...args);
  }

  /**
   * Check if should connect based on role and path
   * @param {number|string} roleId - User role ID
   * @param {string} pathUrl - Current path URL
   * @returns {boolean}
   */
  shouldConnect(roleId, pathUrl = '') {
    if (roleId === null || roleId === '' || roleId === undefined) 
    {
      return false;
    }
    const roleIdNum = parseInt(roleId);
    const hasValidRole = this.allowedRoles.includes(roleIdNum);
    const isExcludedPath = this.excludedPaths.some(path => pathUrl.includes(path));
    
    this.log(`Role check - roleId: ${roleId}, pathUrl: ${pathUrl}, allowed: ${hasValidRole && !isExcludedPath}`);
    
    return hasValidRole && !isExcludedPath;
  }

  /**
   * Initialize and connect to SignalR hub
   * @param {number|string} roleId - User role ID (optional, skip role check if null)
   * @param {string} pathUrl - Current path URL (optional)
   * @returns {Promise<signalR.HubConnection|null>}
   */
  async connect(roleId = null, pathUrl = '') {
    // Check if should connect based on role and path
    this.log('Attempting to connect with roleId:', roleId, 'and pathUrl:', pathUrl);
    if (!this.shouldConnect(roleId, pathUrl)) {
      this.log('Connection skipped due to role or path restrictions');
      return null;
    }

    // If already connected, return existing connection
    if (this.connection && this.isConnected) {
      this.log('Already connected, reusing connection');
      return this.connection;
    }

    // If connection is in progress, wait for it
    if (this.isConnecting && this.connectionPromise) {
      this.log('Connection in progress, waiting...');
      return this.connectionPromise;
    }

    this.isConnecting = true;
    
    this.connectionPromise = this._createConnection();
    
    try {
      const connection = await this.connectionPromise;
      this.isConnecting = false;
      return connection;
    } catch (error) {
      this.isConnecting = false;
      this.connectionPromise = null;
      throw error;
    }
  }

  /**
   * Internal method to create SignalR connection
   * @private
   */
  async _createConnection() {
    this.log('Starting connection creation...');

    const self = this;

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(`${this.urlApi}hub`, {
        transport: signalR.HttpTransportType.WebSockets |
          signalR.HttpTransportType.ServerSentEvents |
          signalR.HttpTransportType.LongPolling,
      })
      .withAutomaticReconnect([5000, 5000, 10000, 10000, 15000])
      .build();

    this.connection.serverTimeoutInMilliseconds = 120000; // 2 minutes

    // Handle connection lifecycle events
    this.connection.onclose((error) => {
      self.log('Connection closed', error);
      self.isConnected = false;
      self._notifyHandlers('connectionClosed', error);
    });

    this.connection.onreconnecting((error) => {
      self.log('Reconnecting...', error);
      self._notifyHandlers('reconnecting', error);
    });

    this.connection.onreconnected((connectionId) => {
      self.log('Reconnected. Connection ID:', connectionId);
      self.isConnected = true;
      self.reconnectAttempts = 0;
      self._notifyHandlers('reconnected', connectionId);
    });

    try {
      await this.connection.start();
      this.log('Connected! Connection ID:', this.connection.connectionId);
      this.isConnected = true;
      this.reconnectAttempts = 0;
      this._registerPendingHandlers();
      this._startPing();
      return this.connection;
    } catch (err) {
      this.logError('Connection Error:', err);
      this.isConnected = false;
      throw err;
    }
  }

  /**
   * Start ping interval to keep connection alive
   * @private
   */
  _startPing() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
    }
    
    const self = this;
    this.pingInterval = setInterval(async () => {
      try {
        if (self.connected) {
          await self.connection.invoke("Ping");
        }
      } catch (error) {
        self.log('Ping error:', error);
        // Try to reconnect if ping fails
        if (!self.connected && self.connection) {
          try {
            await self.connection.start();
            self.isConnected = true;
            self._registerPendingHandlers();
          } catch (reconnectError) {
            self.logError('Reconnection failed:', reconnectError);
          }
        }
      }
    }, 30000); // 30 seconds
  }

  /**
   * Stop ping interval
   * @private
   */
  _stopPing() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  /**
   * Disconnect from SignalR hub
   */
  async disconnect() {
    this._stopPing();
    if (this.connection) {
      try {
        await this.connection.stop();
        this.log('Disconnected!');
      } catch (error) {
        this.logError('Error stopping connection:', error);
      }
      this.isConnected = false;
      this.connection = null;
      this.connectionPromise = null;
    }
  }

  /**
   * Register an event handler for SignalR events
   * @param {string} eventName - The name of the SignalR event
   * @param {string} handlerId - Unique identifier for the handler (e.g., component name)
   * @param {Function} callback - The callback function
   */
  on(eventName, handlerId, callback) {
    if (!this.eventHandlers.has(eventName)) {
      this.eventHandlers.set(eventName, new Map());
      
      // Register the event handler on the connection if connected
      if (this.connection) {
        this.connection.on(eventName, (...args) => {
          this._invokeHandlers(eventName, ...args);
        });
      }
    }
    
    this.eventHandlers.get(eventName).set(handlerId, callback);
  }

  /**
   * Unregister an event handler
   * @param {string} eventName - The name of the SignalR event
   * @param {string} handlerId - Unique identifier for the handler
   */
  off(eventName, handlerId) {
    if (this.eventHandlers.has(eventName)) {
      this.eventHandlers.get(eventName).delete(handlerId);
    }
  }

  /**
   * Unregister all event handlers for a specific handler ID (e.g., when component unmounts)
   * @param {string} handlerId - Unique identifier for the handler
   */
  offAll(handlerId) {
    for (const [eventName, handlers] of this.eventHandlers) {
      handlers.delete(handlerId);
    }
  }

  /**
   * Invoke a method on the SignalR hub
   * @param {string} methodName - The method to invoke
   * @param  {...any} args - Arguments to pass
   */
  async invoke(methodName, ...args) {
    if (!this.connection || !this.isConnected) {
      // Try to reconnect if not connected
      if (this.connection && !this.isConnected) {
        try {
          await this.connection.start();
          this.isConnected = true;
          this._registerPendingHandlers();
          this._startPing();
        } catch (error) {
          throw new Error('SignalR not connected and reconnection failed');
        }
      } else {
        throw new Error('SignalR not connected');
      }
    }
    return await this.connection.invoke(methodName, ...args);
  }

  /**
   * Get connection state
   * @returns {string}
   */
  getState() {
    if (!this.connection) {
      return 'Disconnected';
    }
    return signalR.HubConnectionState[this.connection.state];
  }

  /**
   * Check if connected
   * @returns {boolean}
   */
  get connected() {
    return this.connection && 
           this.connection.state === signalR.HubConnectionState.Connected;
  }

  /**
   * Get connection ID
   * @returns {string|null}
   */
  get connectionId() {
    return this.connection?.connectionId || null;
  }

  /**
   * Internal method to invoke all registered handlers for an event
   * @private
   */
  _invokeHandlers(eventName, ...args) {
    if (this.eventHandlers.has(eventName)) {
      for (const [handlerId, callback] of this.eventHandlers.get(eventName)) {
        try {
          callback(...args);
        } catch (error) {
          console.error(`Error in SignalR event handler [${eventName}][${handlerId}]:`, error);
        }
      }
    }
  }

  /**
   * Internal method to notify connection lifecycle handlers
   * @private
   */
  _notifyHandlers(eventType, data) {
    if (this.eventHandlers.has(`_${eventType}`)) {
      for (const [, callback] of this.eventHandlers.get(`_${eventType}`)) {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in lifecycle handler [${eventType}]:`, error);
        }
      }
    }
  }

  /**
   * Register event handlers on the connection after it's established
   * Called internally when connection is created
   */
  _registerPendingHandlers() {
    if (!this.connection) return;
    
    for (const [eventName] of this.eventHandlers) {
      if (!eventName.startsWith('_')) {
        this.connection.on(eventName, (...args) => {
          this._invokeHandlers(eventName, ...args);
        });
      }
    }
  }
}

// Export singleton instance
const signalRService = new SignalRService();
export default signalRService;
